import type { RowData } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { TableBadgeListCell } from "./TableBadgeListCell";
import { TableBody } from "./TableBody";
import { TableContextProvider } from "./TableContext";
import { TableEmpty } from "./TableEmpty";
import { TableFooter } from "./TableFooter";
import { TableFooterRow } from "./TableFooterRow";
import { TableHeader } from "./TableHeader";
import { TableToolbar } from "./TableToolbar";
import type { TableProps } from "./types";
import { useTableLogic } from "./useTableLogic";

/**
 * A virtualized data grid with a fixed header, proportional resizable columns, per-column overflow
 * behavior, controlled sorting and selection, optional row drag-reorder, and infinite loading.
 *
 * Everything stateful is controlled: the consumer owns sorting, selection and column widths and is
 * told about changes through the matching callbacks. Column widths are proportional shares (CSS grid
 * `fr`), so a layout saved on one screen keeps its proportions on another until a column hits its
 * `minWidth`, at which point the grid scrolls horizontally.
 *
 * `Table.Toolbar` holds the consumer's own search input and action buttons above the grid,
 * `Table.FooterRow` a structural row pinned under the body inside the grid (such as an add action),
 * `Table.Footer` a status line (such as a row count) below the grid, and `Table.Empty` is rendered
 * when there are no rows. `Table.BadgeListCell` is the cell renderer for any list of badges (tags, care
 * team, …): it follows the column's overflow mode on its own, however deeply it is nested in the cell.
 *
 * @example
 * ```tsx
 * <Table
 *   rows={contacts}
 *   columns={columns}
 *   getRowId={(contact) => contact.id}
 *   sorting={sorting}
 *   onSortingChange={setSorting}
 *   columnWidths={widths}
 *   onColumnWidthsChange={saveWidths}
 *   onBottomReached={query.fetchNextPage}
 *   hasMore={query.hasNextPage}
 *   isLoading={query.isLoading}
 *   isLoadingMore={query.isFetchingNextPage}
 *   className="flex-1"
 * >
 *   <Table.Toolbar>
 *     <SearchInput value={query} onChange={setQuery} />
 *     <Button onClick={openConfig}>Configure</Button>
 *   </Table.Toolbar>
 *   <Table.Empty>{t("No contacts match these filters")}</Table.Empty>
 * </Table>
 * ```
 */
const TableRoot = <TRow extends RowData>(props: TableProps<TRow>) => {
  const { t } = useTranslation();
  const { className, height, label, scrollbar = "native", bordered = true, onRowClick, onRowContextMenu } = props;
  const {
    table,
    contextValue,
    headerRef,
    headerViewportRef,
    viewportRef,
    scrollbarGutter,
    minTableWidth,
    onBottomReached,
    isLoading,
    isLoadingMore,
    reorder,
    childrenAbove,
    emptyContent,
    footerRowContent,
    footerContent,
    resetKey,
  } = useTableLogic(props);

  return (
    <TableContextProvider value={contextValue}>
      <div
        data-slot="table"
        className={cn("flex min-h-0 flex-col", className)}
        style={height !== undefined ? { height } : undefined}
      >
        {childrenAbove}
        <div
          // Stands in for `role="table"`: a virtualized CSS grid cannot carry the grid roles without
          // breaking their required parent/child nesting, so tests target the slots instead.
          data-testid="table"
          data-slot="table-grid"
          className={cn("flex min-h-0 flex-1 flex-col", bordered && "overflow-hidden rounded-lg border")}
        >
          <TableHeader
            table={table}
            ref={headerRef}
            viewportRef={headerViewportRef}
            scrollbarGutter={scrollbarGutter}
            minWidth={minTableWidth}
          />
          <TableBody
            table={table}
            viewportRef={viewportRef}
            scrollbar={scrollbar}
            label={label ?? t("Table")}
            minWidth={minTableWidth}
            onBottomReached={onBottomReached}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            emptyContent={emptyContent}
            onRowClick={onRowClick}
            onRowContextMenu={onRowContextMenu}
            reorder={reorder}
            resetKey={resetKey}
          />
          {footerRowContent}
        </div>
        {footerContent}
      </div>
    </TableContextProvider>
  );
};

export const Table = Object.assign(TableRoot, {
  BadgeListCell: TableBadgeListCell,
  Toolbar: TableToolbar,
  FooterRow: TableFooterRow,
  Footer: TableFooter,
  Empty: TableEmpty,
});
